import {
  type PrescriptionPrescriptionTypeEnum,
  type RxByPrincipleSelectableReason,
} from '@redteclab/api/clients/bully'

import {
  addToCartExperimentsTrackProductsOnSuccess,
  type AddToCartSource,
} from '../../experiments/add-to-cart'
import { userApiSwrRefetchMiniCart } from '../../user-data'
import { handleAddToCartErrorApm } from '../api/addToCartErrorApmLogger'
import { useCartIntermediateApiSwrMultipleProductsAddToCartMutation } from '../api/swr/useCartIntermediateApiSwrMultipleProductsAddToCartMutation'
import { cartIntermediateFreezeExpaCookie } from '../cookie-handling/cartIntermediateFreezeExpaCookie'
import { updateIsDataSuccessfullyAddedToCartFromResponse } from '../helpers/updateIsDataSuccessfullyAddedToCartFromResponse'
import { cartIntermediateAccessorGetProductFamilyIds } from '../model/cartIntermediateAccessorGetProductFamilyIds'
import { cartIntermediateAccessorGetProductIdFromCartEntry } from '../model/cartIntermediateAccessorGetProductIdFromCartEntry'
import { cartIntermediateAccessorGetProductTypeContext } from '../model/cartIntermediateAccessorGetProductTypeContext'
import {
  type CartIntermediateEntryObject,
  type CartIntermediateMutationResponseData,
} from '../model/CartIntermediateContextValue'
import {
  cartIntermediateAccessorGetDrawerMessages,
  cartIntermediateAccessorGetIsDataSuccessfullyAddedToCart,
} from '../model/cartIntermediateMutationResponseDataAccessor'

import { useCartIntermediateContext } from './useCartIntermediateContext'

type CartIntermediateMultipleProductsAddToCartProps = {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  products: CartIntermediateEntryObject[]
  rxByPrincipleSelectedReason?: RxByPrincipleSelectableReason
  source: AddToCartSource
}

export const useMultipleProductsAddToCart = ({
  currentPrescriptionType,
  products,
  rxByPrincipleSelectedReason,
  source,
}: CartIntermediateMultipleProductsAddToCartProps): {
  handleAddToCart: () => Promise<void>
  isMutating: boolean
} => {
  const productAddToCartMutation =
    useCartIntermediateApiSwrMultipleProductsAddToCartMutation({
      currentPrescriptionType,
      products,
      rxByPrincipleSelectedReason,
    })
  const {
    openCartIntermediateDrawerWithData,
    resetCartIntermediateContext,
    setIsDataSuccessfullyAddedToCart,
  } = useCartIntermediateContext()

  const handleAddToCart = async (): Promise<void> => {
    resetCartIntermediateContext()
    let response: CartIntermediateMutationResponseData = null

    try {
      response = await productAddToCartMutation.trigger()
      await userApiSwrRefetchMiniCart()
    } catch (error) {
      handleAddToCartErrorApm(error)
    } finally {
      const drawerMessages = cartIntermediateAccessorGetDrawerMessages(response)
      updateIsDataSuccessfullyAddedToCartFromResponse(
        response,
        setIsDataSuccessfullyAddedToCart,
      )
      cartIntermediateFreezeExpaCookie()
      openCartIntermediateDrawerWithData(products, drawerMessages)
    }

    addToCartExperimentsTrackProductsOnSuccess({
      isSuccessfullyAddedToCart:
        cartIntermediateAccessorGetIsDataSuccessfullyAddedToCart(response),
      products: products.map((product) => ({
        offerId: product.id,
        productFamilyIds: cartIntermediateAccessorGetProductFamilyIds({
          product: product.product,
        }),
        productId: cartIntermediateAccessorGetProductIdFromCartEntry({
          cartIntermediateEntryObject: product,
        }),
        productTypeContext: cartIntermediateAccessorGetProductTypeContext({
          product: product.product,
          sellerData: product.sellerData,
        }),
        quantity: 1,
      })),
      source,
    })
  }

  return {
    handleAddToCart,
    isMutating: productAddToCartMutation.isMutating,
  }
}
